/**
 * BrandShareDialog Component (Story 40-8 — Epic 40, roll-up sharing).
 *
 * Centered MODAL DIALOG for sharing a BRAND roll-up dashboard via a public share
 * link.
 *
 * CLONED from apps/web/src/components/dashboards/DashboardShareDialog.tsx and
 * adapted for the BRAND roll-up model. The differences from the single-client
 * mirror:
 *   - Keyed by `brandId` (a brand roll-up has NO single bound client). The
 *     create/list/revoke calls go through lib/brandShareApi pointed at
 *     `/api/brand-share-links`; the create body carries ONLY `dashboardId`
 *     (the server derives brand_id from the dashboard, never the body).
 *   - The single-client PortalAccessControlsCard is OMITTED — it queries a
 *     specific client's integrations/metrics, which a brand roll-up does not have.
 *     Hidden-metric/integration filters are optional server-side, so the v1 brand
 *     share dialog is label + expiration + optional password.
 *   - On create it shows the raw token + copyable `/brand-view/{token}` URL via the
 *     REUSED TokenRevealDialog (which reads only `link.url`).
 *
 * REUSE (import, do not clone): SelectPicker, TokenRevealDialog from
 * components/clients/**.
 */

import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button, Input } from "@provenleap/ui";
import { motion, AnimatePresence } from "motion/react";
import { Trash, LockSimple, Link as LinkIcon } from "@phosphor-icons/react";
import {
  createBrandShareLink,
  listBrandShareLinks,
  revokeBrandShareLink,
  formatBrandShareExpiration,
  type BrandShareExpirationPreset,
  type BrandShareLinkWithToken,
  type BrandShareLinkProjection,
} from "../../lib/brandShareApi";
import type { ToastType } from "../../hooks/useToast";
import { SelectPicker } from "../clients/SelectPicker";
import { TokenRevealDialog } from "../clients/TokenRevealDialog";
import type { PortalLinkWithToken } from "../../lib/portalLinksApi";

interface BrandShareDialogProps {
  /** The saved BRAND roll-up dashboard being shared. */
  dashboardId: string;
  /** The dashboard's owning brand — the share link is brand + dashboard scoped. */
  brandId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showToast: (type: ToastType, message: string) => void;
}

const EXPIRATION_OPTIONS: Array<{
  value: BrandShareExpirationPreset;
  label: string;
}> = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "never", label: "Never expires" },
];

/**
 * Adapt the brand-share link into the `PortalLinkWithToken` shape the REUSED
 * TokenRevealDialog expects. The dialog reads ONLY `link.url`; the other fields
 * are filled with safe defaults (clientId ← brandId) so the (frozen) prop type is
 * satisfied without editing the component.
 */
function toRevealLink(link: BrandShareLinkWithToken): PortalLinkWithToken {
  return {
    ...link,
    clientId: link.brandId,
    scope: "full",
    reportId: null,
    costEfficiencyHidden: false,
  } as PortalLinkWithToken;
}

export function BrandShareDialog({
  dashboardId,
  brandId,
  open,
  onOpenChange,
  showToast,
}: BrandShareDialogProps) {
  const queryClient = useQueryClient();

  const [label, setLabel] = useState("");
  const [expiresAt, setExpiresAt] =
    useState<BrandShareExpirationPreset>("30d");
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [revealLink, setRevealLink] = useState<BrandShareLinkWithToken | null>(
    null,
  );

  // Reset on open (NOT on close) — resetting on close would flash the form to
  // empty values mid-exit-animation.
  useEffect(() => {
    if (open) {
      setLabel("");
      setExpiresAt("30d");
      setUsePassword(false);
      setPassword("");
      setErrors({});
    }
  }, [open]);

  // Existing share links for this brand. The list endpoint is per-BRAND, so we
  // filter to THIS dashboard client-side (a dashboard's links are a subset).
  const linksQuery = useQuery({
    queryKey: ["brand-share-links", brandId],
    enabled: open && Boolean(brandId),
    queryFn: () => listBrandShareLinks(brandId),
  });

  const dashboardLinks = (linksQuery.data ?? []).filter(
    (l) => l.dashboardId === dashboardId,
  );

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (label.trim().length === 0) next.label = "Label is required";
    if (label.length > 120) next.label = "Label must be 120 characters or less";
    if (usePassword && password.length < 8) {
      next.password = "Password must be at least 8 characters";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  const create = useMutation({
    mutationFn: () =>
      createBrandShareLink({
        dashboardId,
        label: label.trim(),
        expiresAt,
        ...(usePassword && password ? { password } : {}),
      }),
    onSuccess: (link) => {
      setRevealLink(link);
      // Reset the form for the next link + refresh the existing-links list.
      setLabel("");
      setUsePassword(false);
      setPassword("");
      void queryClient.invalidateQueries({
        queryKey: ["brand-share-links", brandId],
      });
    },
    onError: (
      err: Error & {
        apiError?: { errors?: Array<{ field: string; message: string }> };
      },
    ) => {
      const fieldErrors = err.apiError?.errors;
      if (Array.isArray(fieldErrors)) {
        const map: Record<string, string> = {};
        for (const e of fieldErrors) map[e.field] = e.message;
        setErrors(map);
      } else {
        showToast("error", err.message ?? "Failed to create share link");
      }
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeBrandShareLink(id),
    onSuccess: () => {
      showToast("success", "Share link revoked");
      void queryClient.invalidateQueries({
        queryKey: ["brand-share-links", brandId],
      });
    },
    onError: (err: Error) =>
      showToast("error", err.message ?? "Failed to revoke link"),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    create.mutate();
  }

  // Block close while submitting (avoids losing in-flight state).
  function handleOpenChange(next: boolean): void {
    if (!next && create.isPending) return;
    onOpenChange(next);
  }

  return (
    <>
      <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
        <AnimatePresence>
          {open && (
            <DialogPrimitive.Portal forceMount>
              <DialogPrimitive.Overlay asChild forceMount>
                <motion.div
                  className="fixed inset-0 z-50 bg-black/35"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                />
              </DialogPrimitive.Overlay>

              <DialogPrimitive.Content asChild forceMount>
                <motion.div
                  className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg max-h-[85vh] overflow-y-auto outline-none"
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                >
                  <div className="flex flex-col space-y-1.5 text-left">
                    <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                      Share brand roll-up
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="text-sm text-muted-foreground">
                      Create a public link to share this live brand roll-up with
                      the brand's owner. You'll see the URL once after generation.
                    </DialogPrimitive.Description>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.05 }}
                    >
                      <label htmlFor="link-label" className="text-sm font-medium">
                        Label <span className="text-destructive">*</span>
                        {errors.label && (
                          <span
                            className="text-sm text-destructive font-normal ml-1"
                            role="alert"
                          >
                            {errors.label}
                          </span>
                        )}
                      </label>
                      <Input
                        id="link-label"
                        value={label}
                        onChange={(e) => {
                          setLabel(e.target.value);
                          if (errors.label)
                            setErrors((prev) => ({ ...prev, label: "" }));
                        }}
                        placeholder="e.g. Q1 roll-up for the brand owner"
                        className={`rounded-lg ${errors.label ? "border-destructive" : ""}`}
                        maxLength={120}
                        data-testid="label-input"
                        autoFocus
                      />
                    </motion.div>

                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <label
                        htmlFor="expires-select"
                        className="text-sm font-medium"
                      >
                        Expiration
                      </label>
                      <SelectPicker
                        id="expires-select"
                        value={expiresAt}
                        onChange={(v) =>
                          setExpiresAt(v as BrandShareExpirationPreset)
                        }
                        options={EXPIRATION_OPTIONS}
                        testId="expires-select"
                      />
                    </motion.div>

                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.15 }}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="use-password"
                          checked={usePassword}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setUsePassword(checked);
                            if (!checked) setPassword("");
                          }}
                          className="rounded"
                          data-testid="use-password"
                        />
                        <label
                          htmlFor="use-password"
                          className="text-sm cursor-pointer"
                        >
                          Require a password
                        </label>
                      </div>
                      {usePassword && (
                        <Input
                          id="link-password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Min 8 characters"
                          className={`rounded-lg ${errors.password ? "border-destructive" : ""}`}
                          minLength={8}
                          data-testid="password-input"
                        />
                      )}
                      {errors.password && (
                        <p className="text-sm text-destructive" role="alert">
                          {errors.password}
                        </p>
                      )}
                    </motion.div>

                    {/* Existing share links for this dashboard */}
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      data-testid="existing-links-section"
                    >
                      <div className="text-sm font-medium">Existing links</div>
                      {linksQuery.isLoading ? (
                        <p className="text-[13px] text-muted-foreground">
                          Loading links…
                        </p>
                      ) : dashboardLinks.length === 0 ? (
                        <p className="text-[13px] text-muted-foreground italic">
                          No share links yet for this roll-up.
                        </p>
                      ) : (
                        <ul className="space-y-2" data-testid="existing-links-list">
                          {dashboardLinks.map((link) => (
                            <ExistingLinkRow
                              key={link.id}
                              link={link}
                              onRevoke={() => revoke.mutate(link.id)}
                              revoking={
                                revoke.isPending && revoke.variables === link.id
                              }
                            />
                          ))}
                        </ul>
                      )}
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.25 }}
                    >
                      <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6">
                        <Button
                          type="button"
                          variant="ghost"
                          className="rounded-xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                          style={{ border: "0.5px solid hsl(var(--input))" }}
                          onClick={() => onOpenChange(false)}
                          disabled={create.isPending}
                        >
                          Close
                        </Button>
                        <Button
                          type="submit"
                          variant="brand"
                          className="rounded-xl cursor-pointer"
                          disabled={create.isPending}
                          data-testid="submit-create-link"
                        >
                          {create.isPending ? "Generating…" : "Generate link"}
                        </Button>
                      </div>
                    </motion.div>
                  </form>
                </motion.div>
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          )}
        </AnimatePresence>
      </DialogPrimitive.Root>

      {/* One-time token reveal — reused from the portal-link surface. */}
      {revealLink && (
        <TokenRevealDialog
          open={revealLink !== null}
          title="Brand roll-up share link created"
          link={toRevealLink(revealLink)}
          onClose={() => setRevealLink(null)}
          showToast={showToast}
        />
      )}
    </>
  );
}

/**
 * One existing share-link row: label, status/expiry summary, and a revoke button.
 */
function ExistingLinkRow({
  link,
  onRevoke,
  revoking,
}: {
  link: BrandShareLinkProjection;
  onRevoke: () => void;
  revoking: boolean;
}) {
  const isRevoked = link.status === "revoked";
  const isExpired = link.status === "expired";
  const statusLabel = isRevoked
    ? "Revoked"
    : isExpired
      ? "Expired"
      : formatBrandShareExpiration(link.expiresAt);

  return (
    <li
      className="flex items-center gap-3 rounded-xl border-[0.5px] border-input bg-background px-3 py-2 shadow-xs"
      data-testid="existing-link-row"
    >
      <LinkIcon size={16} className="shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-[13px] font-medium text-foreground">
            {link.label}
          </span>
          {link.passwordProtected && (
            <LockSimple
              size={13}
              weight="fill"
              className="shrink-0 text-muted-foreground"
              aria-label="Password protected"
            />
          )}
        </div>
        <span
          className={`text-[12px] ${
            isRevoked || isExpired ? "text-muted-foreground" : "text-foreground/60"
          }`}
        >
          {statusLabel}
        </span>
      </div>
      {!isRevoked && (
        <button
          type="button"
          onClick={onRevoke}
          disabled={revoking}
          className="shrink-0 rounded-lg p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors cursor-pointer disabled:opacity-50"
          title="Revoke link"
          aria-label={`Revoke ${link.label}`}
          data-testid="revoke-link-button"
        >
          <Trash size={15} />
        </button>
      )}
    </li>
  );
}
